import { useState } from 'react';
import {
	SourceSystem,
	SourceSystemSchema,
	update,
} from '@/api/doc/source-system';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateSourceSystem({
	sourceSystem,
}: {
	sourceSystem: SourceSystem;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedSourceSystem: SourceSystem) =>
			update(updatedSourceSystem),
		onSuccess: () => {
			toast.success(`Success! The source system has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({ queryKey: ['source-systems'] });
		},
		onError: () => {
			toast.error(`Error! The source system could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<SourceSystem>({
		resolver: zodResolver(SourceSystemSchema),
		defaultValues: sourceSystem,
		mode: 'all',
	});

	const deletedDescription: string = !sourceSystem.deletedOn
		? 'Toggle on to delete this source system.'
		: 'Toggle off to not delete this source system.';

	const deletedLabel: string = !sourceSystem.deletedOn ? 'Delete?' : 'Deleted';

	const onSubmit: SubmitHandler<SourceSystem> = (values: SourceSystem) =>
		mutate(values);

	form.watch();
	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Source System</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent className="overflow-scroll">
					<SheetHeader>
						<SheetTitle>Update Source System</SheetTitle>
					</SheetHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Source System Name</FormLabel>
										<FormControl>
											<Input {...field} placeholder="Source system name" />
										</FormControl>
										<FormDescription>Name the source system</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="deletedOn"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
										<div className="space-y-0.5">
											<FormLabel>{deletedLabel}</FormLabel>
											<FormDescription>{deletedDescription}</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={!!field.value}
												onCheckedChange={() =>
													form.setValue('deletedOn', field.value ? null : new Date())
												}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
